import { TWidget } from 'widgets/Widget';
import { submitFormJson, getJSONByUrl } from 'widgets/toolbox/ajax';
import { WishlistItemsMgr } from '../toolbox/wishlistItemsMgr';
const ALERT_EVENT = 'alert.show';
const WISHLIST_UPDATE_EVENT = 'wishlist.updated';
const wishlistItemsMgr = WishlistItemsMgr.getInstance();

import { TProduct } from 'types/models/product';
import { TProductDetail } from 'widgets/product/ProductDetail';
import { TLabelInstance } from 'widgets/global/Label';
import { TButtonWithIconInstance } from 'widgets/global/ButtonWithIcon';

type TWishlistItem = {
    id: string;
    optionId: string;
    selectedValueId: string;
};

type TResponse = TServerResponse & {
    itemAlreadyExists: boolean;
    msg: string;
    pid: string;
    wishlistItems: Array<TWishlistItem>;
};
type TProductOption = {
    id: string;
    selectedValueId: string;
    customOptionValue: string | null;
    values: Array<{
        isCustomOption: boolean;
        isCustomOptionSelected: boolean;
    }>;
};
type TRemovingItemResponse = {
    success: boolean;
    pid: string;
    wishlistItems: Array<TWishlistItem>;
    itemsCount: number;
};

declare const window: ExtendedWindow;

/**
 * @param Widget Base widget for extending
 * @returns Add to Wishlist Mixin class
 */
function AddToWishlistMixinClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory plugin_wishlist
     * @class AddToWishlistMixin
     * @augments Widget
     * @classdesc Add to wishlist mixin with next features:
     * 1. Represents `Add to wishlist` button together with related functionality
     * <br>*As far as it's a mixin, separate DOM widget is not needed.*
     * 2. Allows to add products to wishlist from PDP, QuickView
     * 3. Communicates to user operation results in both cases: error and success
     *
     * Widget has next relationship:
     * * Listens click on `add button` that instance of {@link Button} to handle addition to wish list.
     * <br>Also uses component methods to change button text, activate/deactivate button
     * * Uses methods of `add message` that instance of {@link Label} to set/hide/show messsage
     * @property {string} data-pid - product id
     * @property {string} [data-text-network-error=Network Error] - Network error message
     * @property {string} [data-add-to-wishlist-msg=addToWishlistMsg] - ID of {@link Label} component that represents `add msg`
     * @property {string} [data-add-to-wishlist-btn=addToWishlist] -  ID of {@link Button} component that represents `add button`
     * @property {string} data-text-added-to-wishlist - Added to wishlist message
     * @property {boolean} [add-to-wishlist-hide-texts=false] - Added to wishlist message
     * @property {string} data-original-wishlist-button-message - original message for `add to wishlist` button
     * @property {boolean} data-show-alert-if-product-in-wishlist - Indicates, if we need to show active icon for wishlist button,
     * in case if request returns, that item already in wishlist
     * @property {string} [data-class-added-to-wishlist] - Added to wishlist class
     */
    class AddToWishlistMixin extends Widget {
        prefs() {
            return {
                pid: '',
                selectedQuantity: '',
                textNetworkError: 'Network Error',
                addToWishlistMsg: 'addToWishlistMsg',
                addToWishlistBtn: 'addToWishlist',
                textAddedToWishlist: '',
                textRemovedFromWishlist: '',
                addToWishlistHideTexts: false,
                originalWishlistButtonMessage: '',
                showAlertIfProductInWishlist: false,
                getWishlistItemsUrl: '',
                accessibilityAlerts: {
                    addedtowishlist: '',
                    removedfromwishlist: ''
                },
                productOptions: <Array<TProductOption>>[],
                detailsLabel: 'detailsMsg',
                allowVgAdding: false,
                ...super.prefs()
            };
        }

        /**
         * @description Initialize widget logic
         */
        init() {
            super.init();
            wishlistItemsMgr
                .isItemInWishlist(this.prefs().pid, this.prefs().productOptions)
                .then((state: boolean) => {
                    this.changeStateWishlistButton(state);
                })
                // eslint-disable-next-line no-console
                .catch(error => console.log(error));
            this.eventBus().on(WISHLIST_UPDATE_EVENT, 'updateStateWishlistButton');
        }

        /**
         * @description Change wishlist icon state on widget update
         */
        onRefresh() {
            wishlistItemsMgr.isItemInWishlist(this.prefs().pid, this.prefs().productOptions).then((state: boolean) => {
                this.changeStateWishlistButton(state);
            });
        }

        /**
         * @description Activate Wish List button
         * @param addToWishlistBtn Target Wish List button
         */
        activateWishListButton(addToWishlistBtn: TButtonWithIconInstance) {
            addToWishlistBtn.activate().press();

            if (!this.prefs().addToWishlistHideTexts) {
                addToWishlistBtn.setText(this.prefs().textAddedToWishlist);
            }
        }

        /**
         * @description Deactivate Wish List button
         * @param addToWishlistBtn Target Wish List button
         */
        deactivateWishListButton(addToWishlistBtn: TButtonWithIconInstance) {
            addToWishlistBtn.deactivate().unpress().enable();

            if (!this.prefs().addToWishlistHideTexts) {
                addToWishlistBtn.setText(this.prefs().originalWishlistButtonMessage);
            }
        }

        /**
         * @description Update state Wish List button after some actions
         * @param updatedProductId Product ID that was updated in the Wish List
         */
        updateStateWishlistButton(updatedProductId: string) {
            if (!updatedProductId) {
                return;
            }

            this.getById(this.prefs().addToWishlistBtn, (addToWishlistBtn: TButtonWithIconInstance) => {
                if (addToWishlistBtn.config.productId !== updatedProductId) {
                    return;
                }

                wishlistItemsMgr.isItemInWishlist(updatedProductId).then((state: boolean) => {
                    if (state && !addToWishlistBtn.isActive()) {
                        this.activateWishListButton(addToWishlistBtn);
                    }

                    if (!state && addToWishlistBtn.isActive()) {
                        this.deactivateWishListButton(addToWishlistBtn);
                    }
                });
            });
        }

        /**
         * @description Change state of Add to Wishlist button
         * @param state Ids product currently in the wishlist
         */
        changeStateWishlistButton(state: boolean) {
            this.getById(this.prefs().addToWishlistBtn, (addToWishlistBtn: TButtonWithIconInstance) => {
                if (state) {
                    this.activateWishListButton(addToWishlistBtn);
                } else {
                    this.deactivateWishListButton(addToWishlistBtn);
                }
            });
        }

        /**
         * @description Removes product from wish list.Change button state
         * @param button Target button
         */
        removeProductFromWishlist(button: TButtonWithIconInstance) {
            button.busy();
            const removeProductFromWishlistUrl = window.sfccData.wishlistActionUrls?.removeFromWishlist;

            getJSONByUrl(removeProductFromWishlistUrl, {
                pid: this.prefs().pid || ''
            })
                .then(response => {
                    const removingItemResponse = <TRemovingItemResponse>response;

                    if (!response.success) {
                        return;
                    }

                    this.changeStateWishlistButton(false);
                    /**
                     * @description Global event to show alert
                     * @event "alert.show"
                     */
                    this.eventBus().emit(ALERT_EVENT, {
                        accessibilityAlert: this.prefs().accessibilityAlerts.removedfromwishlist
                    });
                    wishlistItemsMgr.updateWishlistItems(removingItemResponse.wishlistItems);
                    this.eventBus().emit('product.removedFromWishlist', removingItemResponse.wishlistItems.length > 0);
                    this.eventBus().emit(WISHLIST_UPDATE_EVENT, response.pid);
                    this.eventBus().emit('wishlist.rerender', response);
                    this.showMessage(this.prefs().textRemovedFromWishlist, false);
                })
                .finally(() => {
                    button.unbusy();
                });
        }

        /**
         * @description Handle click on the button
         * @listens dom#click
         * @param button Target button
         */
        wishlistButtonClickHandler(button: TButtonWithIconInstance) {
            if (button.isActive()) {
                this.removeProductFromWishlist(button);
            } else {
                this.addToWishlist(button);
            }
        }

        /**
         * @description afterUpdateProduct handler
         * @param response Response object
         * @param cause Optional cause describing what triggered the update
         * (e.g. "color_switch", "size_selection", "width_selection"), forwarded to
         * analytics so it can distinguish product option changes from full product views
         * @emits "product.updated"
         */
        afterUpdateProduct(response, cause?: string) {
            // @ts-expect-error Method is executed only in scope of ProductDetail widget
            super.afterUpdateProduct(response, cause);

            if (response.wishlistItems) {
                wishlistItemsMgr.updateWishlistItems(response.wishlistItems);
            }
        }

        /**
         * @description Update Product View
         * @param product - Product object
         */
        updateProductView(product: TProduct) {
            // @ts-expect-error Method is executed only in scope of ProductDetail widget
            super.updateProductView(product);
            this.renderWishListButton(product);
            this.hideMessage();
        }

        /**
         * @description Show error/success message
         * @param addButton - Target button
         * @listens dom#click
         */
        addToWishlist(addButton: TButtonWithIconInstance) {
            if (!this.prefs().allowVgAdding && !this.callIfExists('checkUnselectedAttributes')) {
                return null;
            }

            const addToWishlistUrl = window.sfccData.wishlistActionUrls?.addToWishlist;
            const options = this.prefs().productOptions;
            let customOptionValue = '';
            let optionId = '';
            let optionVal = '';

            if (options?.length) {
                optionId = options[0].id;
                optionVal = options[0].selectedValueId;

                if (this.mixedToProductDetails()) {
                    if (this.isCustomOptionValid(options[0].id)) {
                        customOptionValue = <string>this.getCustomOptionValue(options[0].id);
                    } else {
                        return null;
                    }
                }
            }

            addButton.busy();
            this.getById(this.prefs().addToWishlistMsg, (labelAddToWishlistMsg: TLabelInstance) =>
                labelAddToWishlistMsg.hide()
            );

            return submitFormJson(addToWishlistUrl, {
                pid: this.prefs().pid,
                qty: this.prefs().selectedQuantity,
                optionId: optionId,
                optionVal: optionVal,
                customOptionValue: customOptionValue
            })
                .then((response: TResponse) => {
                    this.handleSuccess(response);

                    return response;
                })
                .catch(() => {
                    /**
                     * @description Global event to show alert
                     * @event "alert.error"
                     */
                    // eslint-disable-next-line sonarjs/no-duplicate-string
                    this.eventBus().emit('alert.error', {
                        accessibilityAlert: this.prefs().textNetworkError
                    });
                })
                .finally(() => {
                    addButton.unbusy();
                });
        }

        mixedToProductDetails(): this is InstanceType<TProductDetail> {
            const ProductDetail = <TProductDetail | TWidget>this.getConstructor('productDetail');
            const BaseWidget = this.getConstructor('widget');

            return ProductDetail !== BaseWidget && this instanceof ProductDetail;
        }

        /**
         * @description Handle success response
         * @param response - Response object
         */
        handleSuccess(response: TResponse) {
            const addToWishlistBtn = <TButtonWithIconInstance>(
                this.getById(this.prefs().addToWishlistBtn, element => element)
            );

            if (!addToWishlistBtn) {
                return;
            }

            if (response.success) {
                this.activateWishListButton(addToWishlistBtn);
                const accessibilityAlert = this.prefs().accessibilityAlerts.addedtowishlist;

                /**
                 * @description Global event to show alert
                 * @event "alert.show"
                 */
                this.eventBus().emit(ALERT_EVENT, {
                    accessibilityAlert
                });
            } else if (response.itemAlreadyExists) {
                this.deactivateWishListButton(addToWishlistBtn);

                if (this.prefs().showAlertIfProductInWishlist) {
                    /**
                     * @description Global event to show alert
                     * @event "alert.error"
                     */
                    this.eventBus().emit('alert.error', {
                        accessibilityAlert: response.errorMessage
                    });
                }
            } else {
                /**
                 * @description Global event to show alert
                 * @event "alert.error"
                 */
                this.eventBus().emit('alert.error', { errorCode: 500 });
            }

            wishlistItemsMgr.updateWishlistItems(response.wishlistItems);
            this.eventBus().emit('product.addedToWishlist', response.wishlistItems.length > 0, true);
            this.showMessage(this.prefs().textAddedToWishlist, false);
            this.eventBus().emit(WISHLIST_UPDATE_EVENT, response.pid);
        }

        /**
         * @description Method to revert initial state for `Add to Wishlist` button and messages
         * Usually used when re-rendering PDP/QuickView after variation change
         *
         * @param product Product object
         */
        renderWishListButton(product: TProduct) {
            this.setPref('pid', product.id);
            wishlistItemsMgr.isItemInWishlist(product.id, product.options).then((state: boolean) => {
                this.changeStateWishlistButton(state);
            });
        }

        /**
         * @description Renders message
         * @param msg Message
         * @param error Error flag
         */
        showMessage(msg: string, error?: boolean) {
            this.getById(this.prefs().detailsLabel, label => {
                label.render('template', { msg, error }, label.ref('container')).then(() => {
                    label.show();
                });
            });
        }

        /**
         * @description Hide the message
         */
        hideMessage() {
            this.getById(this.prefs().detailsLabel, label => {
                label.toggle(false);
            });
        }
    }

    return AddToWishlistMixin;
}

export type TAddToWishlistMixin = ReturnType<typeof AddToWishlistMixinClassCreator>;

export type TAddToWishlistMixinInstance = InstanceType<TAddToWishlistMixin>;

export default AddToWishlistMixinClassCreator;
